/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { AriaAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { ToolbarStyles } from './themes/ToolbarStyles.css';
import { useRovingTabIndexContainer } from '../hooks/PRIVATE_useRovingTabIndexContainer';
import { TabbableModeContext, useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { ToolbarRedwoodTheme } from './themes/redwood/ToolbarTheme';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { classNames } from '#utils/UNSAFE_classNames';

type AriaProps = Pick<AriaAttributesSignalExcluded, 'aria-controls' | 'aria-label'>;

type ToolbarProps = AriaProps & {
  /**
   * Specifies the children.
   */
  children: ComponentChildren;
};

export const Toolbar = ({
  'aria-controls': ariaControls,
  'aria-label': ariaLabel,
  children
}: ToolbarProps) => {
  const { isTabbable } = useTabbableMode();
  const { focusedId, rovingTabIndexContainerProps } = useRovingTabIndexContainer(!isTabbable);
  const { classes, styles } = useComponentTheme<never, ToolbarStyles>(ToolbarRedwoodTheme);
  const toolbarClasses = classNames([classes, styles.base]);

  return (
    <div
      aria-controls={ariaControls}
      aria-label={ariaLabel}
      class={toolbarClasses}
      role="toolbar"
      {...rovingTabIndexContainerProps}>
      <TabbableModeContext.Provider
        value={{ isTabbable: isTabbable, isRoving: true, focusedId: focusedId }}>
        {children}
      </TabbableModeContext.Provider>
    </div>
  );
};
